<template>
  <tr
    class="v2-affiliates-row"
    :class="{ 'is-expanded': isOpen }"
    role="button"
    tabindex="0"
    :aria-expanded="isOpen"
    @click="toggleOpen"
    @keydown.enter.prevent="toggleOpen"
    @keydown.space.prevent="toggleOpen"
    @mouseenter="hovered = true"
    @mouseleave="hovered = false"
  >
    <td class="v2-affiliates-row__cell v2-affiliates-row__cell--name">
      <div class="v2-affiliates-row__user">
        <div class="v2-affiliates-row__avatar" aria-hidden="true">
          <img
            v-if="hasValidAvatar"
            :src="affiliate.profilePicture"
            :alt="fullName"
            class="v2-affiliates-row__avatar-img"
          />
          <span v-else class="v2-affiliates-row__avatar-initials">
            {{ initials }}
          </span>
        </div>
        <div class="v2-affiliates-row__user-text">
          <p class="v2-affiliates-row__user-name">{{ fullName }}</p>
          <p class="v2-affiliates-row__user-email">
            {{ affiliate.email || "Sin correo" }}
          </p>
        </div>
      </div>
    </td>
    <td class="v2-affiliates-row__cell">
      <span class="v2-affiliates-row__muted">{{ createdAtLabel }}</span>
    </td>
    <td class="v2-affiliates-row__cell">
      <StatusBadge :label="statusLabel" :variant="statusVariant" />
    </td>
    <td class="v2-affiliates-row__cell v2-affiliates-row__cell--actions">
      <div
        class="v2-affiliates-row__actions"
        :class="{ 'is-visible': hovered || alwaysShowActions || isOpen }"
      >
        <button
          v-if="isPending"
          type="button"
          class="v2-affiliates-row__action"
          title="Reenviar invitación"
          @click.stop="$emit('resend', affiliate)"
        >
          <v-icon size="20">mdi-email-arrow-right-outline</v-icon>
        </button>
        <button
          type="button"
          class="v2-affiliates-row__action"
          title="Editar"
          @click.stop="$emit('edit', affiliate)"
        >
          <v-icon size="20">mdi-pencil-outline</v-icon>
        </button>
        <button
          type="button"
          class="v2-affiliates-row__action v2-affiliates-row__action--danger"
          title="Eliminar"
          @click.stop="$emit('delete', affiliate)"
        >
          <v-icon size="20">mdi-trash-can-outline</v-icon>
        </button>
      </div>
    </td>
  </tr>
</template>

<script>
import StatusBadge from "@/components/memberDashboardV2/ui/StatusBadge.vue";
import {
  formatAffiliateDate,
  formatFieldValue,
  getAffiliateFullName,
  getAffiliateInitials,
  getStatusLabel,
  getStatusVariant,
  isAffiliatePending,
} from "../affiliatesHelpers";

export default {
  name: "AffiliateRow",
  components: { StatusBadge },
  props: {
    affiliate: { type: Object, required: true },
    alwaysShowActions: { type: Boolean, default: false },
    expanded: { type: Boolean, default: false },
  },
  data() {
    return {
      hovered: false,
    };
  },
  computed: {
    isOpen() {
      return Boolean(this.expanded);
    },
    fullName() {
      return getAffiliateFullName(this.affiliate);
    },
    initials() {
      return getAffiliateInitials(this.affiliate);
    },
    hasValidAvatar() {
      const pic = this.affiliate && this.affiliate.profilePicture;
      return Boolean(pic) && pic !== "NOT_ASSIGNED" && pic !== "N/D";
    },
    statusVariant() {
      return getStatusVariant(this.affiliate.validationStatus);
    },
    statusLabel() {
      return getStatusLabel(this.affiliate.validationStatus);
    },
    createdAtLabel() {
      return formatAffiliateDate(this.affiliate.createdAt);
    },
    isPending() {
      return isAffiliatePending(this.affiliate);
    },
  },
  methods: {
    formatFieldValue,
    toggleOpen() {
      this.$emit("toggle", this.affiliate);
    },
  },
};
</script>

<style scoped>
.v2-affiliates-row {
  transition: background-color var(--transition-fast);
  cursor: pointer;
}

.v2-affiliates-row:hover {
  background-color: var(--color-surface-low);
}

.v2-affiliates-row:focus-visible {
  outline: 2px solid var(--color-amber);
  outline-offset: -2px;
}

.v2-affiliates-row.is-expanded {
  background-color: var(--color-surface-low);
}

.v2-affiliates-row__cell {
  padding: var(--space-3) var(--space-4);
  border-bottom: 1px solid var(--color-border);
  vertical-align: middle;
  color: var(--color-text);
  font-size: var(--fs-body-sm);
}

.v2-affiliates-row__cell--name {
  min-width: 260px;
}

.v2-affiliates-row__user {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  min-width: 0;
}

.v2-affiliates-row__avatar {
  width: 40px;
  height: 40px;
  border-radius: var(--radius-full);
  background-color: var(--color-primary-soft);
  color: var(--color-primary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  font-family: var(--font-headline);
  font-weight: var(--fw-semibold);
  font-size: var(--fs-label-md);
}

.v2-affiliates-row__avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.v2-affiliates-row__avatar-initials {
  line-height: 1;
}

.v2-affiliates-row__user-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 2px;
}

.v2-affiliates-row__user-name {
  margin: 0;
  font-family: var(--font-body);
  font-weight: var(--fw-semibold);
  color: var(--color-text);
  font-size: var(--fs-body-sm);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 240px;
}

.v2-affiliates-row__user-email {
  margin: 0;
  font-size: var(--fs-caption);
  color: var(--color-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 240px;
}

.v2-affiliates-row__muted {
  color: var(--color-text-muted);
  font-size: var(--fs-body-sm);
}

.v2-affiliates-row__cell--actions {
  text-align: right;
  width: 1%;
  white-space: nowrap;
}

.v2-affiliates-row__actions {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  opacity: 0;
  transition: opacity var(--transition-fast);
}

.v2-affiliates-row__actions.is-visible {
  opacity: 1;
}

.v2-affiliates-row__action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 0;
  background: transparent;
  border-radius: var(--radius-md);
  color: var(--color-text-muted);
  cursor: pointer;
  transition: background-color var(--transition-fast), color var(--transition-fast);
}

.v2-affiliates-row__action:hover {
  background-color: var(--color-primary-soft);
  color: var(--color-primary);
}

.v2-affiliates-row__action:focus-visible {
  outline: 2px solid var(--color-amber);
  outline-offset: 2px;
}

.v2-affiliates-row__action--danger:hover {
  background-color: var(--color-error-soft-2);
  color: var(--color-error);
}

.v2-affiliates-row:last-child .v2-affiliates-row__cell {
  border-bottom: 0;
}

@media (max-width: 768px) {
  .v2-affiliates-row__actions {
    opacity: 1;
  }
}
</style>
